import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  BoxProps,
  Drawer,
  DrawerProps,
  Collapse,
  LinearProgress,
  LinearProgressProps,
  Toolbar,
  IconButton,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useDispatch } from 'react-redux';
import { TP_COLORS } from '../../../constants';
import { getThemeMode } from '../../../store/ConfigSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { getIsLoading } from '../../../store/LoaderSlice';
import {
  getUsageByUserId,
  saveUserDetails,
} from '../../../store/AccessLimitSlice/AccessLimitApi';
import { createPremiumSession } from '../../../store/StripeSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import { useAppSelector } from '../../../hooks';
import {
  checkUserPermission,
  isMobileScreen,
  SafeAreaGaps,
} from '../../../helper';
import { checkUserNavigationBarEnabled } from '../../../navigation/helper';
import DrawerContents, { drawerWidth } from './components/DrawerContents';
import NavBarContents, {
  navBarWidth,
  navBarCollapseWith,
} from './components/NavBarContents';
import DashboardView from './DashboardView';
import NavigationBarHeader from './components/NavigationBarHeader';
// import MyQuiz from '../MyQuiz';
import MyQuizHistory from '../MyQuizHistory';
import MyQuizHistorySubmissions from '../MyQuizHistory/MyQuizHistorySubmissions';
import QuizHistoryViewQuestions from '../ManageSubmissionQuestions/QuizHistoryViewQuestions';
import InvalidRoute from '../../Validate/InvalidRoute';
import ViewSubmissionsBySubTopic from '../ViewSubmissionsBySubTopic';
import Resources from '../Resources/Resources';
import Students from '../../AdminPages/Students';
import StudentSubmissions from '../../AdminPages/Students/StudentSubmissions';
import ViewSubmissionQuestions from '../../AdminPages/Students/ViewSubmissionQuestions';
import ManageSubmissionQuestions from '../ManageSubmissionQuestions';
import SubmissionHistoryForQuiz from '../MyQuizHistory/SubmissionHistoryForQuiz';
import ViewQuestion from '../MyQuizHistory/ViewQuestion';
import Diagnostic from '../Diagnostic';
import LeadGenModal, { ILeadGenState } from './modals/LeadGenModal';
import PremiumModal from './modals/PremiumModal';

interface IState {
  isNavBarExpanded: boolean;
  barWidth: number;
}

const initialState: IState = {
  isNavBarExpanded: true,
  barWidth: navBarWidth,
};

const UserDashboard: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Store
  const themeMode = useAppSelector(getThemeMode);
  const authState = useAppSelector(getAuthState);
  const isLoading = useAppSelector(getIsLoading);
  const accessLimitState = useAppSelector(getAccessLimitState);

  const authUser = authState.user;
  // State
  const [state, setState] = useState<IState>(initialState);

  // Permissions
  const viewQuizPermission = checkUserPermission(authState.user, {
    quizHistory: ['view'],
  });

  const viewSubmissionPermission = checkUserPermission(authState.user, {
    submission: ['view'],
  });

  const adminStudentPermission = checkUserPermission(authState.user, {
    adminStudent: ['view'],
  });

  const isMobile = isMobileScreen();
  const isNavigationBarEnabled = checkUserNavigationBarEnabled(location);
  const mainStyles: BoxProps['sx'] = {
    flexGrow: 1,
    width: { sm: `calc(100% - ${isMobile ? drawerWidth : 0}px)` },
    backgroundColor:
      themeMode === 'light' ? TP_COLORS.paperGray.light : 'background.paper',
    color: 'text.primary',
    minHeight: '100vh',
  };

  useEffect(() => {
    if (
      authUser &&
      (authUser.role.name === 'Student' || authUser.role.name === 'Guest')
    ) {
      dispatch(getUsageByUserId(authUser._id));
    }
    const urlPattern = /\/dashboard\/my-quizzes\/subtopic\/[a-z0-9]{24}\/[a-z0-9]{24}\/[a-z0-9_-]+_[a-z0-9]{24}$/;
    if (urlPattern.test(location.pathname)) {
      setState({
        isNavBarExpanded: false,
        barWidth: navBarCollapseWith,
      });
    }
  }, []);

  const handleLeadGenSubmit = (values: ILeadGenState) => {
    if (values && authUser) {
      dispatch(
        saveUserDetails({
          ...values,
          userId: authUser._id,
        })
      );
    }
    dispatch(toggleLeadGenModal());
  };

  const handlePremiumModalSubmit = (priceId: string) => {
    if (authUser) {
      dispatch(
        createPremiumSession({
          userId: authUser._id,
          priceId,
        })
      );
    }
    dispatch(togglePremiumModal());
  };

  const RENDER_ROUTES = () => {
    return (
      <Routes>
        <Route path="/" element={<DashboardView />} />

        {/* My Quizzes */}
        {/* {viewQuizPermission && <Route path="my-quizzes" element={<MyQuiz />} />} */}

        {viewSubmissionPermission && (
          <>
            <Route
              path="my-quizzes/subtopic/:subtopicId/:submissionId/:pattern"
              element={<ManageSubmissionQuestions />}
            />
            <Route
              path="my-quizzes/subtopic/:pattern"
              element={<ViewSubmissionsBySubTopic />}
            />
            <Route
              path="my-quizzes/resources/subtopic/:pattern"
              element={<Resources />}
            />
            <Route
              path="my-quizzes/diagnostic/:subtopicId/:diagnosticId/:pattern"
              element={<Diagnostic />}
            />
          </>
        )}
        {/* Quiz History */}
        {viewQuizPermission && (
          <Route path="quiz-history" element={<MyQuizHistory />} />
        )}
        {viewSubmissionPermission && (
          <>
            <Route
              path="quiz-history/questions/:submissionId/:pattern"
              element={<ViewQuestion />}
            />
            <Route
              path="quiz-history/submissions/:quizId/:pattern"
              element={<SubmissionHistoryForQuiz />}
            />
            <Route
              path="quiz-history/submissions/:quizId"
              element={<MyQuizHistorySubmissions />}
            />
            <Route
              path="quiz-history/questions/:submissionId"
              element={<QuizHistoryViewQuestions />}
            />
          </>
        )}
        {/* Students */}
        {adminStudentPermission && (
          <>
            <Route path="students" element={<Students />} />
            <Route
              path="students/submissions/:userId"
              element={<StudentSubmissions />}
            />
            <Route
              path="students/viewQuestions/:userId/:submissionId"
              element={<ViewSubmissionQuestions />}
            />
          </>
        )}
        {/* Validate */}
        <Route path="*" element={<InvalidRoute />} />
      </Routes>
    );
  };

  const RENDER_MODALS = () => {
    return (
      <>
        {accessLimitState.leadGenModalOpen && (
          <LeadGenModal
            open={accessLimitState.leadGenModalOpen}
            onClose={() => dispatch(toggleLeadGenModal())}
            onConfirm={handleLeadGenSubmit}
          />
        )}
        {accessLimitState.premiumModalOpen && (
          <PremiumModal
            open={accessLimitState.premiumModalOpen}
            onClose={() => dispatch(togglePremiumModal())}
            onConfirm={handlePremiumModalSubmit}
          />
        )}
      </>
    );
  };

  if (isNavigationBarEnabled) {
    const loaderStyles: LinearProgressProps['sx'] = {
      position: 'fixed',
      zIndex: 1,
      top: `calc(${SafeAreaGaps.top} + 64px)`,
      marginLeft: SafeAreaGaps.left,
    };

    const navStyles: BoxProps['sx'] = {
      width: { sm: state.barWidth },
      flexShrink: { sm: 0 },
      transition: 'width 0.5s',
      visibility: { xs: 'hidden', sm: 'visible' },
    };

    const drawerStyles: DrawerProps['sx'] = {
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: state.barWidth,
        transition: 'width 0.5s',
      },
    };

    return (
      <Box>
        <CssBaseline />

        {/* Header */}
        <NavigationBarHeader
          appBarProps={{ sx: { display: { xs: 'none', sm: 'block' } } }}
        />
        <Toolbar sx={{ display: { xs: 'none', sm: 'block' } }} />

        {/* Loader */}
        <Collapse in={isLoading} sx={loaderStyles}>
          <LinearProgress color="info" />
        </Collapse>

        <Box sx={{ display: 'flex' }}>
          {!isMobile && (
            <Box component="nav" sx={navStyles}>
              <Drawer
                open
                variant="persistent"
                sx={drawerStyles}
                PaperProps={{
                  sx: {
                    backgroundColor:
                      themeMode === 'light' ? 'background.paper' : '#191d20',
                    top: '64px',
                    height: 'calc(100% - 64px)',
                    overflowY: 'auto',
                  },
                }}
              >
                <div
                  style={{
                    paddingTop: `calc(${SafeAreaGaps.top})`,
                    paddingBottom: `calc(${SafeAreaGaps.bottom} + 15px)`,
                    paddingLeft: `calc(${SafeAreaGaps.top} + 15px)`,
                    paddingRight: `calc(${SafeAreaGaps.right} + 15px)`,
                    height: '100%',
                  }}
                >
                  <div
                    style={{
                      visibility: state.isNavBarExpanded ? 'visible' : 'hidden',
                    }}
                  >
                    <NavBarContents isNavBarExpanded={state.isNavBarExpanded} />
                  </div>
                  <div
                    style={{
                      minWidth: '50px',
                      visibility: state.isNavBarExpanded ? 'hidden' : 'visible',
                    }}
                  />
                </div>
              </Drawer>
              <IconButton
                style={{
                  zIndex: 1200,
                  padding: '3px',
                  position: 'fixed',
                  top: '100px',
                  transition: 'all 0.5s',
                  left: `calc(${state.barWidth}px - 15px)`,
                }}
                sx={{
                  backgroundColor: 'background.default',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: TP_COLORS.white,
                  },
                }}
                onClick={() =>
                  setState((prevState) => ({
                    ...prevState,
                    isNavBarExpanded: !prevState.isNavBarExpanded,
                    barWidth:
                      prevState.barWidth === navBarWidth
                        ? navBarCollapseWith
                        : navBarWidth,
                  }))
                }
              >
                {state.isNavBarExpanded ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </Box>
          )}

          <Box component="main" sx={mainStyles}>
            {RENDER_ROUTES()}
            {RENDER_MODALS()}
          </Box>
        </Box>
      </Box>
    );
  }

  // Styles
  const loaderStyles: LinearProgressProps['sx'] = {
    position: 'fixed',
    zIndex: 1,
    top: SafeAreaGaps.top,
    marginTop: SafeAreaGaps.top,
    marginLeft: SafeAreaGaps.left,
    width: { sm: `calc(100% - ${isMobile ? drawerWidth : 0}px)` },
  };

  const navStyles: BoxProps['sx'] = {
    width: { sm: drawerWidth },
    flexShrink: { sm: 0 },
  };

  const drawerStyles: DrawerProps['sx'] = {
    display: { xs: 'none', sm: 'block' },
    '& .MuiDrawer-paper': {
      boxSizing: 'border-box',
      width: drawerWidth,
    },
  };

  // RENDER
  return (
    <div>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />

        {/* Drawer */}
        {!isMobile && (
          <Box component="nav" sx={navStyles}>
            <Drawer
              open
              variant="permanent"
              sx={drawerStyles}
              PaperProps={{
                sx: {
                  backgroundColor:
                    themeMode === 'light' ? 'background.paper' : '#191d20',
                },
              }}
            >
              <DrawerContents />
            </Drawer>
          </Box>
        )}

        {/* Body */}
        <Box component="main" sx={mainStyles}>
          <Collapse in={isLoading} sx={loaderStyles}>
            <LinearProgress color="info" />
          </Collapse>
          {RENDER_ROUTES()}
          {RENDER_MODALS()}
        </Box>
      </Box>
    </div>
  );
};

export default UserDashboard;
